/**
 * One device's release history (Phase C ledger), opened from its row in the
 * ONE fleet table. A modal, not a page: the device stays managed in
 * `/pos-config` and nothing here duplicates the fleet list.
 *
 * Custom modal — never a native `confirm()`/`alert()`. Light surface to match
 * the fleet card; RUBY theme, never purple.
 */
import { useEffect, useState } from 'react';
import { X, History, AlertTriangle } from 'lucide-react';
import { deviceConfigApi } from '../../../services/deviceConfigApi';
import { dedupeLedgerEntries, type FleetLedgerEntry } from './fleetRows';
import { RubyLoader } from '../../../components/RubyLoader';

const LIMIT = 100; // server default is 50, cap 200

export default function DeviceHistoryModal({
  deviceType,
  deviceId,
  deviceLabel,
  onClose,
}: {
  deviceType: string;
  deviceId: string;
  deviceLabel: string;
  onClose: () => void;
}) {
  const [entries, setEntries] = useState<FleetLedgerEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(0);

  useEffect(() => {
    let live = true;
    deviceConfigApi
      .listDeviceLedger(deviceType, deviceId, LIMIT)
      .then((r) => {
        if (!live) return;
        const raw = (r?.entries || []) as FleetLedgerEntry[];
        // Phase C known limit: a tight concurrent check-in window can record
        // the same transition twice. De-duplicate at READ time — the audit
        // rows themselves are never rewritten.
        const clean = dedupeLedgerEntries(raw);
        setCollapsed(raw.length - clean.length);
        setEntries(clean);
      })
      .catch((e: any) => { if (live) setError(e?.message || 'Could not load release history'); });
    return () => { live = false; };
  }, [deviceType, deviceId]);

  // Escape closes. A custom modal that traps the user until they find the X is
  // the thing a native dialog gives for free and this one must give explicitly.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/60 p-0 md:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Release history"
      // Backdrop click closes; clicks inside the panel must not bubble out to
      // it, or selecting text in the history would dismiss the modal.
      onClick={onClose}
    >
      <div
        className="w-full md:max-w-2xl bg-white rounded-t-2xl md:rounded-2xl shadow-xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 p-4 border-b border-gray-100">
          <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center shrink-0">
            <History size={18} className="text-rose-600" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-extrabold text-gray-900 truncate">Release history</div>
            <div className="text-[11.5px] text-gray-500 truncate">{deviceLabel} · {deviceId}</div>
          </div>
          <button onClick={onClose} aria-label="Close"
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-900 hover:bg-gray-100">
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto p-4">
          {error && (
            <div className="flex items-start gap-2 text-[12.5px] text-red-600 bg-red-50 rounded-xl p-3">
              <AlertTriangle size={15} className="mt-0.5 shrink-0" /> <span>{error}</span>
            </div>
          )}
          {!error && entries === null && <RubyLoader variant="section" label="Loading history…" />}
          {!error && entries !== null && entries.length === 0 && (
            <div className="text-[12.5px] text-gray-400 py-6 text-center">
              No version changes recorded yet. History starts at the device's first check-in that reports an app version.
            </div>
          )}
          {!error && entries !== null && entries.length > 0 && (
            <ol className="space-y-2">
              {entries.map((e, i) => (
                <li key={`${e.at}-${i}`} className="flex items-start gap-3 rounded-xl border border-gray-100 p-3">
                  <div className="text-[11px] text-gray-400 w-32 shrink-0">{new Date(e.at).toLocaleString()}</div>
                  <div className="min-w-0">
                    <div className="text-[12.5px] font-bold text-gray-900">
                      {e.fromVersion ? `${e.fromVersion} → ${e.toVersion}` : `First seen on ${e.toVersion}`}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      {e.appId ? `${e.appId} · ` : ''}via {e.via || 'unknown'} · target {e.source || 'unknown'}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
          {collapsed > 0 && (
            <div className="mt-3 text-[11px] text-gray-400">
              {collapsed} duplicate {collapsed === 1 ? 'row' : 'rows'} collapsed — the same transition recorded twice by
              overlapping check-ins.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
